import { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
    MessageSquareText, Clock, CheckCircle2, XCircle, AlertTriangle,
    Send, Loader2, Gift, X, ShieldCheck
} from 'lucide-react';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';
import api from '../../lib/api';
import { parseUtc } from '../../lib/datetime';

const formatTimestamp = (iso) => {
    try {
        const d = parseUtc(iso);
        return d.toLocaleString('en-US', {
            month: 'short', day: 'numeric',
            hour: 'numeric', minute: '2-digit', hour12: true
        });
    } catch {
        return '';
    }
};

const formatTimeRemaining = (iso) => {
    try {
        const exp = parseUtc(iso);
        const now = new Date();
        const hoursLeft = Math.max(0, (exp - now) / (1000 * 60 * 60));
        if (hoursLeft <= 0) return 'Expired';
        if (hoursLeft < 1) return `${Math.floor(hoursLeft * 60)}m remaining`;
        if (hoursLeft < 24) return `${Math.floor(hoursLeft)}h remaining`;
        return `${Math.floor(hoursLeft / 24)}d ${Math.floor(hoursLeft % 24)}h remaining`;
    } catch {
        return '';
    }
};

const STATUS_CONFIG = {
    active: { label: 'Active', color: 'bg-emerald-100 text-emerald-700', icon: MessageSquareText },
    inactive: { label: 'Inactive', color: 'bg-amber-100 text-amber-700', icon: Clock },
    resolved: { label: 'Resolved', color: 'bg-blue-100 text-blue-700', icon: CheckCircle2 },
    stopped: { label: 'Stopped', color: 'bg-slate-100 text-slate-600', icon: XCircle },
    expired: { label: 'Expired', color: 'bg-red-100 text-red-700', icon: AlertTriangle },
};

export function RecoveryConversationPanel({ sessionId, onClose, onStatusChange }) {
    const { t } = useTranslation();
    const [session, setSession] = useState(null);
    const [loading, setLoading] = useState(true);
    const [messageText, setMessageText] = useState('');
    const [sending, setSending] = useState(false);
    const [resolving, setResolving] = useState(false);
    const [showGiftCard, setShowGiftCard] = useState(false);
    const [giftCardValue, setGiftCardValue] = useState('25');
    const [giftCardExpiry, setGiftCardExpiry] = useState('90');
    const [giftCardNote, setGiftCardNote] = useState('');
    const [issuingGiftCard, setIssuingGiftCard] = useState(false);
    const [giftCardEnabled, setGiftCardEnabled] = useState(false);
    const messagesEndRef = useRef(null);
    const pollRef = useRef(null);

    const fetchSession = useCallback(async () => {
        try {
            const res = await api.get(`/recovery/${sessionId}`);
            setSession(res.data);
            // Check if gift card is enabled for this card
            try {
                const cardsRes = await api.get('/cards');
                const card = cardsRes.data.find(c => c.card_id === res.data.card_id);
                setGiftCardEnabled(card?.gift_card_enabled || false);
            } catch {}
        } catch (err) {
            if (err.response?.status === 410) {
                setSession({ status: 'expired' });
            } else if (err.response?.status === 404) {
                setSession({ status: 'archived' });
            } else {
                toast.error('Failed to load session');
            }
        } finally {
            setLoading(false);
        }
    }, [sessionId]);

    useEffect(() => {
        fetchSession();
        pollRef.current = setInterval(fetchSession, 15000);
        return () => clearInterval(pollRef.current);
    }, [fetchSession]);

    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [session?.messages]);

    const handleSendMessage = async () => {
        if (!messageText.trim() || sending) return;
        setSending(true);
        try {
            await api.post(`/recovery/${sessionId}/message`, { text: messageText.trim() });
            setMessageText('');
            await fetchSession();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to send message');
        } finally {
            setSending(false);
        }
    };

    const handleResolve = async () => {
        setResolving(true);
        try {
            await api.put(`/recovery/${sessionId}/resolve`);
            toast.success('Session resolved and archived');
            onStatusChange?.('resolved');
            onClose?.();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to resolve session');
        } finally {
            setResolving(false);
        }
    };

    const handleIssueGiftCard = async () => {
        const val = parseFloat(giftCardValue);
        if (!val || val <= 0 || val > 500) {
            toast.error('Gift card value must be between $0.01 and $500');
            return;
        }
        setIssuingGiftCard(true);
        try {
            const res = await api.post('/gift-cards/issue', {
                value: val,
                currency: 'CAD',
                expiry_days: parseInt(giftCardExpiry) || 90,
                note: giftCardNote || undefined,
                recovery_session_id: sessionId,
                card_id: session?.card_id,
                organization_id: session?.organization_id,
                location_id: session?.location_id,
            });
            toast.success(`Gift card issued: ${res.data.code} ($${res.data.value})`);
            setShowGiftCard(false);
            setGiftCardValue('25');
            setGiftCardNote('');
            await fetchSession();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to issue gift card');
        } finally {
            setIssuingGiftCard(false);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64" data-testid="conversation-loading">
                <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
            </div>
        );
    }

    if (!session || session.status === 'archived') {
        return (
            <div className="text-center py-12 text-muted-foreground" data-testid="conversation-archived">
                <ShieldCheck className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm">This session has been archived.</p>
                <Button variant="ghost" size="sm" onClick={onClose} className="mt-3">Close</Button>
            </div>
        );
    }

    const isActive = session.status === 'active' || session.status === 'inactive';
    const statusConf = STATUS_CONFIG[session.status] || STATUS_CONFIG.expired;
    const StatusIcon = statusConf.icon;
    const messages = session.messages || [];

    return (
        <div className="flex flex-col h-full" data-testid="conversation-panel">
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b bg-muted/30" data-testid="conversation-header">
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                        <h3 className="text-sm font-semibold truncate">
                            {session.organization_name || 'Recovery Session'}
                        </h3>
                        <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium ${statusConf.color}`} data-testid="conversation-status">
                            <StatusIcon className="w-3 h-3" />
                            {statusConf.label}
                        </span>
                    </div>
                    <div className="flex items-center gap-3 text-xs text-muted-foreground">
                        {session.location_name && <span>{session.location_name}</span>}
                        {session.customer_email_masked && <span>{session.customer_email_masked}</span>}
                        {session.expires_at && isActive && (
                            <span className="flex items-center gap-1" data-testid="conversation-expiry">
                                <Clock className="w-3 h-3" />
                                {formatTimeRemaining(session.expires_at)}
                            </span>
                        )}
                    </div>
                </div>
                <Button variant="ghost" size="icon" onClick={onClose} className="h-7 w-7 shrink-0" data-testid="conversation-close">
                    <X className="w-4 h-4" />
                </Button>
            </div>

            {/* Feedback Context */}
            {session.feedback_rating && (
                <div className="px-4 py-2 border-b bg-muted/10 text-xs text-muted-foreground" data-testid="conversation-feedback-context">
                    <span className="font-medium">Original feedback:</span>{' '}
                    Rating {session.feedback_rating}/5
                    {session.feedback_category && ` — ${session.feedback_category}`}
                    {session.feedback_message && (
                        <span className="block mt-0.5 italic truncate">"{session.feedback_message}"</span>
                    )}
                </div>
            )}

            {/* Messages */}
            <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3" data-testid="conversation-messages">
                {messages.length === 0 && (
                    <p className="text-center text-muted-foreground text-xs py-8">No messages yet. Send the first message to the customer.</p>
                )}
                {messages.map((msg) => {
                    const isSystem = msg.sender === 'system';
                    const isManager = msg.sender === 'manager';
                    const isGiftCard = msg.type === 'gift_card';

                    if (isGiftCard) {
                        return (
                            <div key={msg.id} className="flex justify-center" data-testid={`msg-gift-${msg.id}`}>
                                <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 max-w-xs text-center">
                                    <Gift className="w-5 h-5 text-amber-600 mx-auto mb-1" />
                                    <p className="text-xs font-semibold text-amber-800">{t('recoveryConversation.giftCardIssued')}</p>
                                    <p className="text-lg font-bold text-amber-700 my-1">
                                        ${msg.gift_card_value?.toFixed(2)} {msg.gift_card_currency}
                                    </p>
                                    <p className="text-xs text-amber-600 font-mono">{msg.gift_card_code}</p>
                                    <p className="text-xs text-amber-500 mt-1">{formatTimestamp(msg.timestamp)}</p>
                                </div>
                            </div>
                        );
                    }

                    return (
                        <div
                            key={msg.id}
                            className={`flex ${isManager ? 'justify-end' : isSystem ? 'justify-center' : 'justify-start'}`}
                            data-testid={`msg-${msg.id}`}
                        >
                            <div className={`max-w-[75%] rounded-xl px-3 py-2 ${
                                isManager
                                    ? 'bg-primary text-primary-foreground'
                                    : isSystem
                                    ? 'bg-muted text-muted-foreground text-center'
                                    : 'bg-muted'
                            }`}>
                                <p className="text-xs font-medium opacity-70 mb-0.5">
                                    {isManager ? (msg.sender_name || 'You') : isSystem ? 'System' : 'Customer'}
                                </p>
                                <p className="text-sm leading-relaxed whitespace-pre-wrap">{msg.text}</p>
                                <p className="text-xs opacity-50 mt-1">{formatTimestamp(msg.timestamp)}</p>
                            </div>
                        </div>
                    );
                })}
                <div ref={messagesEndRef} />
            </div>

            {/* Gift Card Issue Form */}
            {showGiftCard && isActive && (
                <div className="border-t bg-amber-50/50 px-4 py-3 space-y-2" data-testid="gift-card-form">
                    <div className="flex items-center justify-between">
                        <Label className="text-xs font-semibold flex items-center gap-1">
                            <Gift className="w-3.5 h-3.5 text-amber-600" />
                            Issue Gift Card
                        </Label>
                        <Button variant="ghost" size="icon" className="h-5 w-5" onClick={() => setShowGiftCard(false)}>
                            <X className="w-3 h-3" />
                        </Button>
                    </div>
                    <div className="flex gap-2">
                        <div className="flex-1">
                            <Label className="text-xs text-muted-foreground">Value ($)</Label>
                            <Input
                                type="number"
                                min="1" max="500" step="5"
                                value={giftCardValue}
                                onChange={e => setGiftCardValue(e.target.value)}
                                className="h-8 text-sm"
                                data-testid="gift-card-value"
                            />
                        </div>
                        <div className="w-28">
                            <Label className="text-xs text-muted-foreground">Expiry</Label>
                            <Select value={giftCardExpiry} onValueChange={setGiftCardExpiry}>
                                <SelectTrigger className="h-8 text-sm" data-testid="gift-card-expiry">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="30">30 days</SelectItem>
                                    <SelectItem value="60">60 days</SelectItem>
                                    <SelectItem value="90">90 days</SelectItem>
                                    <SelectItem value="180">180 days</SelectItem>
                                    <SelectItem value="365">365 days</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                    <Input
                        placeholder="Internal note (optional)"
                        value={giftCardNote}
                        onChange={e => setGiftCardNote(e.target.value)}
                        className="h-8 text-sm"
                        data-testid="gift-card-note"
                    />
                    <Button
                        size="sm"
                        onClick={handleIssueGiftCard}
                        disabled={issuingGiftCard}
                        className="w-full bg-amber-600 hover:bg-amber-700 text-white h-8 text-xs"
                        data-testid="gift-card-issue-btn"
                    >
                        {issuingGiftCard ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Gift className="w-3 h-3 mr-1" />}
                        Issue Gift Card
                    </Button>
                </div>
            )}

            {/* Input + Actions */}
            {isActive && (
                <div className="border-t px-4 py-3 space-y-2" data-testid="conversation-input-area">
                    <div className="flex gap-2">
                        <Textarea
                            value={messageText}
                            onChange={e => setMessageText(e.target.value)}
                            placeholder="Write a message..."
                            rows={2}
                            maxLength={2000}
                            className="flex-1 resize-none text-sm"
                            onKeyDown={e => {
                                if (e.key === 'Enter' && !e.shiftKey) {
                                    e.preventDefault();
                                    handleSendMessage();
                                }
                            }}
                            data-testid="conversation-message-input"
                        />
                        <Button
                            onClick={handleSendMessage}
                            disabled={!messageText.trim() || sending}
                            size="icon"
                            className="h-auto self-end"
                            data-testid="conversation-send-btn"
                        >
                            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                        </Button>
                    </div>
                    <div className="flex items-center gap-2">
                        {giftCardEnabled && (
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setShowGiftCard(!showGiftCard)}
                                className="text-xs h-7 text-amber-700 border-amber-300 hover:bg-amber-50"
                                data-testid="conversation-gift-card-trigger"
                            >
                                <Gift className="w-3 h-3 mr-1" />
                                Gift Card
                            </Button>
                        )}
                        <div className="flex-1" />
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={handleResolve}
                            disabled={resolving}
                            className="text-xs h-7 text-emerald-700 border-emerald-300 hover:bg-emerald-50"
                            data-testid="conversation-resolve-btn"
                        >
                            {resolving ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <CheckCircle2 className="w-3 h-3 mr-1" />}
                            Mark Resolved
                        </Button>
                    </div>
                </div>
            )}

            {/* Closed session footer */}
            {!isActive && (
                <div className="border-t px-4 py-3 bg-muted/30 text-center" data-testid="conversation-closed-footer">
                    <p className="text-xs text-muted-foreground">
                        This session is {session.status}. No further messages can be sent.
                    </p>
                </div>
            )}
        </div>
    );
}
